import React, {useState, useEffect, useRef} from 'react';
import {Modal, Form, Input, InputNumber, Select, DatePicker, message, Row, Col, Card, Button, Space, Divider, List, Tag, Badge} from 'antd';
import {PlusOutlined, MinusOutlined, DeleteOutlined, ShoppingCartOutlined} from "@ant-design/icons";
import {Radio} from "antd";
import {createOrder, generateOrderNo} from "../../../lib/api_cw_orders";
import {getList} from "../../../lib/api_data";
import {getStocks} from "../../../lib/api_stock";
import moment from "moment";
import DictRadioGroup from "../../../components/dict/dict-radio-group";

const {Meta} = Card;

const OrderPosModal = ({onCancel, onOk, storeFilter}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [generatingOrderNo, setGeneratingOrderNo] = useState(false);
    const [selectedStore, setSelectedStore] = useState(storeFilter || '门店1');
    const [stocks, setStocks] = useState([]);
    const [cartItems, setCartItems] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [discountAmount, setDiscountAmount] = useState(0);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const lastRequestDateRef = useRef('');

    // 组件挂载时生成订单号和获取商品列表
    useEffect(() => {
        if (storeFilter) {
            form.setFieldsValue({ md: storeFilter });
        }
        generateOrderNumber();
        fetchStocks();
    }, [storeFilter, form]);

    // 门店选项
    const storeOptions = [
        { label: '门店1', value: '门店1' },
        { label: '门店2', value: '门店2' }
    ];

    // 获取商品列表
    const fetchStocks = () => {
        getStocks(1, 10000) // 获取前10000个商品
            .then((res) => {
                if (res.code === 0) {
                    setStocks(res.data.list || []);
                }
            })
            .catch(() => {
                message.error('获取商品列表失败');
            });
    };

    // 生成订单号
    const generateOrderNumber = (date = null) => {
        if (generatingOrderNo) return;
        
        const requestDate = date ? date : moment().format('YYYY-MM-DD');
        if (lastRequestDateRef.current === requestDate) {
            return;
        }
        
        lastRequestDateRef.current = requestDate;
        setGeneratingOrderNo(true);
        
        generateOrderNo(date)
            .then(res => {
                if (res.code === 0) {
                    form.setFieldsValue({
                        order_no: res.data.orderNo
                    });
                } else {
                    message.error(res.message || '生成订单号失败');
                    const dateStr = date ? moment(date).format('YYYYMMDD') : moment().format('YYYYMMDD');
                    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
                    form.setFieldsValue({
                        order_no: dateStr + random
                    });
                }
            })
            .catch(() => {
                message.error('生成订单号失败');
                const dateStr = date ? moment(date).format('YYYYMMDD') : moment().format('YYYYMMDD');
                const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
                form.setFieldsValue({
                    order_no: dateStr + random
                });
            })
            .finally(() => {
                setGeneratingOrderNo(false);
            });
    };

    // 当订单日期变化时重新生成订单号
    const handleDateChange = (date) => {
        if (date) {
            const formattedDate = date.format('YYYY-MM-DD');
            setTimeout(() => {
                generateOrderNumber(formattedDate);
            }, 500);
        }
    };

    // 添加商品到购物车
    const addToCart = (stock) => {
        const existingItem = cartItems.find(item => item.id === stock.id);
        
        if (existingItem) {
            // 如果商品已存在，增加数量
            setCartItems(cartItems.map(item => 
                item.id === stock.id 
                    ? {...item, quantity: item.quantity + 1}
                    : item
            ));
        } else {
            // 如果商品不存在，添加到购物车
            setCartItems([...cartItems, {
                ...stock,
                quantity: 1,
                selectedPrice: stock.price, // 默认使用商品价格
                discountAmount: 0 // 初始优惠金额为0
            }]);
        }
    };

    // 修改购物车商品数量
    const updateCartItemQuantity = (itemId, newQuantity) => {
        if (newQuantity <= 0) {
            removeFromCart(itemId);
            return;
        }
        
        setCartItems(cartItems.map(item => 
            item.id === itemId 
                ? {...item, quantity: newQuantity}
                : item
        ));
    };

    // 从购物车移除商品
    const removeFromCart = (itemId) => {
        setCartItems(cartItems.filter(item => item.id !== itemId));
    };

    // 计算总价
    const calculateTotal = () => {
        return cartItems.reduce((total, item) => {
            const itemTotal = (item.selectedPrice * item.quantity) - (item.discountAmount || 0);
            return total + Math.max(0, itemTotal);
        }, 0);
    };

    // 计算每个商品的最终价格
    const calculateItemFinalPrice = (item) => {
        return Math.max(0, item.selectedPrice - (item.discountAmount || 0) / item.quantity);
    };

    // 计算原价
    const calculateOriginalTotal = () => {
        return cartItems.reduce((total, item) => {
            return total + (item.price * item.quantity);
        }, 0);
    };

    // 计算优惠金额分摊
    const calculateDiscountDistribution = () => {
        if (cartItems.length === 0 || discountAmount <= 0) {
            return cartItems;
        }

        const originalTotal = calculateOriginalTotal();
        if (originalTotal <= 0) return cartItems;

        // 按商品原价比例分摊优惠金额
        let remainingDiscount = discountAmount;
        const updatedItems = cartItems.map((item, index) => {
            const itemOriginalTotal = item.price * item.quantity;
            const itemDiscountRatio = itemOriginalTotal / originalTotal;
            
            // 如果是最后一个商品，使用剩余优惠金额
            if (index === cartItems.length - 1) {
                return {
                    ...item,
                    discountAmount: remainingDiscount
                };
            }
            
            // 计算该商品的优惠金额
            const itemDiscount = Math.round(itemDiscountRatio * discountAmount * 100) / 100;
            remainingDiscount -= itemDiscount;
            
            return {
                ...item,
                discountAmount: itemDiscount
            };
        });

        return updatedItems;
    };

    // 更新购物车商品优惠金额
    useEffect(() => {
        if (cartItems.length > 0 && discountAmount > 0) {
            const updatedItems = calculateDiscountDistribution();
            setCartItems(updatedItems);
        }
    }, [discountAmount, cartItems.length]);

    // 自动生成备注：商品名称*数量，多个商品用逗号隔开
    const generateRemark = () => {
        if (cartItems.length === 0) return '';
        return cartItems.map(item => `${item.name}*${item.quantity}`).join(',');
    };

    // 当购物车商品变化时，自动更新备注
    useEffect(() => {
        const remark = generateRemark();
        if (remark) {
            form.setFieldsValue({ remark });
        }
    }, [cartItems, form]);

    // 过滤商品列表
    const filteredStocks = stocks.filter(stock => {
        const matchesSearch = 
            stock.name.toLowerCase().includes(searchText.toLowerCase()) ||
            (stock.category && stock.category.toLowerCase().includes(searchText.toLowerCase()));
        
        const matchesCategory = !selectedCategory || stock.category === selectedCategory;
        
        return matchesSearch && matchesCategory;
    });

    const handleSubmit = () => {
        if (cartItems.length === 0) {
            message.warning('请先选择商品');
            return;
        }

        form.validateFields()
            .then(values => {
                setLoading(true);
                
                // 构建订单数据
                const orderData = {
                    ...values,
                    md: selectedStore, // 使用单选组选择的门店
                    order_date: values.order_date ? values.order_date.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
                    original_amount: calculateOriginalTotal(),
                    discount_amount: discountAmount,
                    total_amount: calculateTotal(),
                    actual_amount: calculateTotal(), // 实际支付金额等于总金额
                    order_status: 2, // 默认设为已支付
                    order_items: cartItems.map(item => ({
                        stock_id: item.id,
                        stock_name: item.name,
                        unit_price: item.selectedPrice,
                        quantity: item.quantity,
                        subtotal: item.selectedPrice * item.quantity,
                        discount_amount: item.discountAmount || 0,
                        actual_amount: (item.selectedPrice * item.quantity) - (item.discountAmount || 0)
                    }))
                };

                createOrder(orderData)
                    .then(res => {
                        if (res.code === 0) {
                            message.success('创建订单成功');
                            onOk && onOk();
                        } else {
                            message.error(res.message || '创建订单失败');
                        }
                    })
                    .catch(() => {
                        message.error('创建订单失败');
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            })
            .catch(info => {
                console.log('Validate Failed:', info);
            });
    };

    const handleCancel = () => {
        onCancel && onCancel();
    };

    return (
        <Modal
            title="添加订单"
            visible={true}
            onOk={handleSubmit}
            onCancel={handleCancel}
            confirmLoading={loading}
            destroyOnClose={true}
            width={1000}
            style={{ top: 20 }}
            bodyStyle={{ maxHeight: '70vh', overflow: 'auto' }}
        >
            <Row gutter={16}>
                {/* 左侧：商品列表 */}
                <Col span={12}>
                    <Card 
                        title="商品列表" 
                        extra={
                            <Input.Search
                                placeholder="搜索商品名称或分类"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                style={{width: 200}}
                            />
                        }
                        style={{height: '500px', overflow: 'auto'}}
                    >
                        <div style={{marginBottom: 16}}>
                            <DictRadioGroup
                                type_value="stocks_category"
                                value={selectedCategory}
                                onChange={setSelectedCategory}
                                optionType="button"
                            />
                        </div>
                        <Row gutter={[16, 16]}>
                            {filteredStocks.map(stock => (
                                <Col span={8} key={stock.id}>
                                    <Card
                                        hoverable
                                        onClick={() => addToCart(stock)}
                                        bodyStyle={{padding: '16px', textAlign: 'center'}}
                                    >
                                        <div style={{fontSize: '16px', fontWeight: 'bold', marginBottom: '8px'}}>
                                            {stock.name}
                                        </div>
                                        <div style={{fontSize: '18px', color: '#1890ff', fontWeight: 'bold'}}>
                                            ¥{stock.price}
                                        </div>
                                        {stock.quantity > 0 && (
                                            <Tag color="green" style={{marginTop: '8px'}}>库存: {stock.quantity}</Tag>
                                        )}
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </Card>
                </Col>

                {/* 右侧：购物车和订单信息 */}
                <Col span={12}>
                    <div style={{display: 'flex', flexDirection: 'column', height: '500px'}}>
                        {/* 购物车 */}
                        <Card 
                            title={
                                <Space>
                                    <ShoppingCartOutlined />
                                    购物车
                                    <Badge count={cartItems.reduce((total, item) => total + item.quantity, 0)} />
                                </Space>
                            }
                            style={{flex: 1, marginBottom: '16px', display: 'flex', flexDirection: 'column'}}
                        >
                            {/* 购物车商品列表 */}
                            <div style={{flex: 1, overflow: 'auto', marginBottom: '16px'}}>
                                {cartItems.length === 0 ? (
                                    <div style={{textAlign: 'center', color: '#999', padding: '40px'}}>
                                        <ShoppingCartOutlined style={{fontSize: '48px', marginBottom: '16px'}} />
                                        <div>购物车为空</div>
                                    </div>
                                ) : (
                                    <List
                                        dataSource={cartItems}
                                        renderItem={item => (
                                            <List.Item
                                                actions={[
                                                    <Button 
                                                        type="link" 
                                                        icon={<DeleteOutlined />} 
                                                        onClick={() => removeFromCart(item.id)}
                                                        danger
                                                    />
                                                ]}
                                            >
                                                <List.Item.Meta
                                                title={item.name}
                                                description={
                                                    <Space direction="vertical" size="small" style={{width: '100%'}}>
                                                        <Space>
                                                            <Button 
                                                                size="small" 
                                                                icon={<MinusOutlined />}
                                                                onClick={() => updateCartItemQuantity(item.id, item.quantity - 1)}
                                                            />
                                                            <InputNumber 
                                                                size="small" 
                                                                min={1}
                                                                value={item.quantity}
                                                                style={{width: '60px'}}
                                                                onChange={(value) => updateCartItemQuantity(item.id, value)}
                                                            />
                                                            <Button 
                                                                size="small" 
                                                                icon={<PlusOutlined />}
                                                                onClick={() => updateCartItemQuantity(item.id, item.quantity + 1)}
                                                            />
                                                            <span>¥{item.selectedPrice}</span>
                                                            <span>× {item.quantity}</span>
                                                            <span>= ¥{(item.selectedPrice * item.quantity).toFixed(2)}</span>
                                                        </Space>
                                                        {item.discountAmount > 0 && (
                                                            <Space style={{color: '#ff4d4f', fontSize: '12px'}}>
                                                                <span>优惠: -¥{item.discountAmount.toFixed(2)}</span>
                                                                <span>实付: ¥{((item.selectedPrice * item.quantity) - item.discountAmount).toFixed(2)}</span>
                                                            </Space>
                                                        )}
                                                    </Space>
                                                }
                                            />
                                            </List.Item>
                                        )}
                                    />
                                )}
                            </div>

                            {/* 价格汇总 */}
                            <div style={{borderTop: '1px solid #f0f0f0', paddingTop: '16px'}}>
                                <Space direction="vertical" style={{width: '100%'}}>
                                    <Row justify="space-between">
                                        <Col>原价:</Col>
                                        <Col>¥{calculateOriginalTotal().toFixed(2)}</Col>
                                    </Row>
                                    <Row justify="space-between" align="middle">
                                        <Col>优惠:</Col>
                                        <Col>
                                            <InputNumber 
                                                size="small"
                                                min={0}
                                                max={calculateOriginalTotal()}
                                                value={discountAmount}
                                                onChange={setDiscountAmount}
                                                style={{width: '100px'}}
                                                prefix="-¥"
                                                precision={2}
                                            />
                                        </Col>
                                    </Row>
                                    <Divider />
                                    <Row justify="space-between" style={{fontSize: '16px', fontWeight: 'bold'}}>
                                        <Col>总计:</Col>
                                        <Col>¥{calculateTotal().toFixed(2)}</Col>
                                    </Row>
                                </Space>
                            </div>
                        </Card>

                        {/* 订单基本信息 */}
                        <Card style={{flexShrink: 0}}>
                            <Form
                                form={form}
                                layout="vertical"
                                size="small"
                                initialValues={{
                                    order_date: moment()
                                }}
                            >
                                <Row gutter={8}>
                                    <Col span={12}>
                                        <Form.Item
                                            label="订单号"
                                            name="order_no"
                                        >
                                            <Input 
                                                placeholder={generatingOrderNo ? "正在生成订单号..." : "自动生成订单号"} 
                                                readOnly 
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            label="订单日期"
                                            name="order_date"
                                        >
                                            <DatePicker 
                                                style={{width: '100%'}} 
                                                format="YYYY-MM-DD" 
                                                onChange={handleDateChange}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Form.Item
                                    label="门店"
                                    name="md"
                                    rules={[
                                        {
                                            required: true,
                                            message: '请选择门店',
                                        },
                                    ]}
                                >
                                    <Radio.Group 
                                        options={storeOptions}
                                        optionType="button"
                                        buttonStyle="solid"
                                    />
                                </Form.Item>
                                <Row gutter={8}>
                                    <Col span={12}>
                                        <Form.Item
                                            label="客户姓名"
                                            name="customer_name"
                                        >
                                            <Input placeholder="请输入客户姓名" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            label="客户电话"
                                            name="customer_phone"
                                        >
                                            <Input placeholder="请输入客户电话" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Form.Item
                                    label="备注"
                                    name="remark"
                                >
                                    <Input.TextArea rows={2} placeholder="请输入备注信息" />
                                </Form.Item>
                            </Form>
                        </Card>
                    </div>
                </Col>
            </Row>
        </Modal>
    );
};

export default OrderPosModal;